<template>

    <Toast/>

    <MenuFinancialCompact v-if="userStore.user?.roles?.includes('ROLE_ISORISE')" />
    <MenuHealthCompact v-else />

    <div class="px-6 surface-overlay relative">

        <div v-show="activeTab === 1" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <ul class="list-none py-2 px-0 m-0 flex align-items-center font-medium overflow-x-auto w-full">
                        <li class="pr-3"><a class="cursor-pointer">Configuration</a></li>
                        <li class="px-2"><i class="pi pi-angle-right text-900"></i></li>
                        <li class="px-2"><a class="cursor-pointer text-900 white-space-nowrap">Availability</a></li>
                    </ul>

                    <h3>Manage Availability</h3>

                    <div class="grid">
                        <ul class="list-none p-0 m-0 flex lg:align-items-center flex-column lg:flex-row select-none">
                            <li>
                                <a @click="callNewAvailability" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Availability Slot</span>
                                </a>
                            </li>
                            <li>
                                <a @click="callNewNonAvailability" v-ripple
                                   class="flex p-3 align-items-center text-600 hover:text-900 hover:surface-100 font-medium border-round cursor-pointer transition-colors transition-duration-150 p-ripple">
                                    <i class="pi pi-plus-circle mr-2"></i>
                                    <span>New Non-Availability Slot</span>
                                </a>
                            </li>
                        </ul>
                    </div>

                    <DataTable
                        :value="availability"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['locationName', 'practitionerName', 'billingPlanName']">

                        <template #header>
                            <div class="p-grid">
                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Location, Practitioner or Billing Plan"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No availability found.
                        </template>

                        <template #loading>
                            Loading availability. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['menu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredAvailabilityItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <Column field="locationName" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Location
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.locationName }}
                                </div>
                            </template>
                        </Column>

                        <Column field="practitionerName" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Practitioner
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.practitionerName }}
                                </div>
                            </template>
                        </Column>

                        <Column field="billingPlanName" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Billing Plan
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.billingPlanName }}
                                </div>
                            </template>
                        </Column>

                        <Column field="dayOfWeek" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Day of Week
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    <span v-if="data.dayOfWeek === 1"
                                          :class="'submission-badge status-' + data.dayOfWeek">Monday</span>
                                    <span v-if="data.dayOfWeek === 2"
                                          :class="'submission-badge status-' + data.dayOfWeek">Tuesday</span>
                                    <span v-if="data.dayOfWeek === 3"
                                          :class="'submission-badge status-' + data.dayOfWeek">Wednesday</span>
                                    <span v-if="data.dayOfWeek === 4"
                                          :class="'submission-badge status-' + data.dayOfWeek">Thursday</span>
                                    <span v-if="data.dayOfWeek === 5"
                                          :class="'submission-badge status-' + data.dayOfWeek">Friday</span>
                                    <span v-if="data.dayOfWeek === 6"
                                          :class="'submission-badge status-' + data.dayOfWeek">Saturday</span>
                                    <span v-if="data.dayOfWeek === 7"
                                          :class="'submission-badge status-' + data.dayOfWeek">Sunday</span>
                                </div>
                            </template>
                        </Column>

                        <Column field="startHour" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Start Hour
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.startHour }}:00
                                </div>
                            </template>
                        </Column>

                        <Column field="endHour" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    End Hour
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.endHour }}:00
                                </div>
                            </template>
                        </Column>

                        <Column field="status" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Status
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                </div>
                            </template>
                        </Column>

                    </DataTable>

                    <div style="height: 20px;"></div>

                    <h3>Manage Non-Availability</h3>

                    <DataTable
                        :value="nonAvailability"
                        :paginator="true"
                        class="p-datatable-gridlines"
                        :rows="100"
                        dataKey="id"
                        :rowsPerPageOptions="[10,20,50,100]"
                        :rowHover="true"
                        :striped-rows="true"
                        :reorderableColumns="true"
                        :sortMode="'multiple'"
                        v-model:filters="filters"
                        filterDisplay="menu"
                        :loading="loading"
                        responsiveLayout="scroll"
                        :globalFilterFields="['locationName', 'practitionerName', 'billingPlanName']">

                        <template #header>
                            <div class="p-grid">
                                <div class="p-col-12 p-md-2">
                                    <span class="p-input-icon-left">
                                        <i class="pi pi-search"/>
                                        <InputText v-model="filters['global'].value"
                                                   placeholder="Keyword Search on Location, Practitioner or Billing Plan"
                                                   style="width: 100%"/>
                                    </span>
                                </div>
                            </div>

                        </template>

                        <template #empty>
                            No non-availability found.
                        </template>

                        <template #loading>
                            Loading non-availability. Please wait.
                        </template>

                        <Column
                            field="selectAnOption"
                            header=""
                            :headerStyle="{ width: '2rem' }"
                            :bodyStyle="{ width: '2rem', padding: '0.25rem' }">
                            <template #body="{ data, index }">
                                <Button icon="pi pi-ellipsis-v" class="p-button-sm p-button-text p-button-rounded m-0"
                                        @click="$refs['menu' + index].toggle($event)"/>
                                <Menu
                                    :ref="'menu' + index"
                                    :model="getFilteredNonAvailabilityItems(data)"
                                    :popup="true"/>
                            </template>
                        </Column>

                        <Column field="locationName" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Location
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.locationName }}
                                </div>
                            </template>
                        </Column>

                        <Column field="practitionerName" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Practitioner
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.practitionerName }}
                                </div>
                            </template>
                        </Column>

                        <Column field="billingPlanName" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Billing Plan
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ data.billingPlanName }}
                                </div>
                            </template>
                        </Column>

                        <Column field="startDate" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Start Date
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ formatDate(data.startDate) }}
                                </div>
                            </template>
                        </Column>

                        <Column field="endDate" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    End Date
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    {{ formatDate(data.endDate) }}
                                </div>
                            </template>
                        </Column>

                        <Column field="status" :sortable="true">
                            <template #header>
                                <div class="flex justify-content-center align-items-center w-full">
                                    Status
                                </div>
                            </template>

                            <template #body="{ data }">
                                <div class="flex justify-content-center align-items-center w-full">
                                    <span v-if="data.status === 'A'"
                                          :class="'status-badge status-' + data.status">Active</span>
                                    <span v-if="data.status === 'I'"
                                          :class="'status-badge status-' + data.status">Inactive</span>
                                </div>
                            </template>
                        </Column>

                    </DataTable>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 2" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Availability</h3>

                    <div class="field">
                        <label for="location">Location</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="existingAvailability.locationName" :suggestions="filteredLocations"
                                      @complete="searchLocations($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="practitioner">Practitioner</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="existingAvailability.practitionerName"
                                      :suggestions="filteredPractitioners"
                                      @complete="searchPractitioners($event)" field="fullName"/>
                    </div>

                    <div class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="existingAvailability.billingPlanName" :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlans($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="dayOfWeek">Day of Week</label>
                        <Dropdown v-model="existingAvailability.dayOfWeek" :options="dayOfWeekTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Value for Day of Week"/>
                    </div>

                    <div class="field">
                        <label for="startHour">Start Hour</label>
                        <InputNumber id="horizontal" v-model="existingAvailability.startHour" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="endHour">End Hour</label>
                        <InputNumber id="horizontal" v-model="existingAvailability.endHour" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="existingAvailability.status" :options="statusTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" @click="updateAvailability"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 3" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Availability</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Availability?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteAvailability"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 4" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Availability Slot</h3>

                    <div class="field">
                        <label for="location">Location</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newAvailability.location" :suggestions="filteredLocations"
                                      @complete="searchLocations($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="practitioner">Practitioner</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newAvailability.practitioner" :suggestions="filteredPractitioners"
                                      @complete="searchPractitioners($event)" field="fullName"/>
                    </div>

                    <div class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newAvailability.billingPlan" :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlans($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="dayOfWeek">Day of Week</label>
                        <Dropdown v-model="newAvailability.dayOfWeek" :options="dayOfWeekTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Value for Day of Week"/>
                    </div>

                    <div class="field">
                        <label for="startHour">Start Hour</label>
                        <InputNumber id="horizontal" v-model="newAvailability.startHour" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <div class="field">
                        <label for="endHour">End Hour</label>
                        <InputNumber id="horizontal" v-model="newAvailability.endHour" showButtons
                                     buttonLayout="horizontal"
                                     :step="1"
                                     :min="0"
                                     decrementButtonClass="p-button-danger"
                                     incrementButtonClass="p-button-success"
                                     incrementButtonIcon="pi pi-plus" decrementButtonIcon="pi pi-minus"
                                     mode="decimal"/>
                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createAvailability"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 5" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>New Non-Availability Slot</h3>

                    <div class="field">
                        <label for="location">Location</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newNonAvailability.location" :suggestions="filteredLocations"
                                      @complete="searchLocations($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="practitioner">Practitioner</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newNonAvailability.practitioner" :suggestions="filteredPractitioners"
                                      @complete="searchPractitioners($event)" field="fullName"/>
                    </div>

                    <div class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="newNonAvailability.billingPlan" :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlans($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="startDate">Start Date</label>
                        <Calendar id="startDate" v-model="parsedStartDate" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <div class="field">
                        <label for="endDate">End Date</label>
                        <Calendar id="endDate" v-model="parsedEndDate" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>

                    </div>

                    <Button label="Create" class="mr-2 mb-2" @click="createNonAvailability"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 6" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Update Non-Availability</h3>

                    <div class="field">
                        <label for="location">Location</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="existingNonAvailability.locationName" :suggestions="filteredLocations"
                                      @complete="searchLocations($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="practitioner">Practitioner</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="existingNonAvailability.practitionerName"
                                      :suggestions="filteredPractitioners"
                                      @complete="searchPractitioners($event)" field="fullName"/>
                    </div>

                    <div class="field">
                        <label for="billingPlan">Billing Plan</label>
                        <AutoComplete placeholder="Search" :dropdown="true" :multiple="false"
                                      v-model="existingNonAvailability.billingPlanName" :suggestions="filteredBillingPlans"
                                      @complete="searchBillingPlans($event)" field="name"/>
                    </div>

                    <div class="field">
                        <label for="startDate">Start Date</label>
                        <Calendar id="startDate" v-model="parsedExistingStartDate" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                    </div>

                    <div class="field">
                        <label for="endDate">End Date</label>
                        <Calendar id="endDate" v-model="parsedExistingEndDate" required="true"
                                  :showTime="true"
                                  :showSeconds="false" :showIcon="true" dateFormat="yy-mm-dd"/>
                    </div>

                    <div class="field">
                        <label for="status">Status</label>
                        <Dropdown v-model="existingNonAvailability.status" :options="statusTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select an Option"/>
                    </div>

                    <Button label="Update" class="mr-2 mb-2" @click="updateNonAvailability"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

        <div v-show="activeTab === 7" class="grid">
            <div class="col-12 md:col-12">
                <div class="card p-fluid">

                    <h3>Delete Non-Availability</h3>

                    <div class="field">
                        <label for="deleteConfirmationResponse">Do You Wish to Delete this Non-Availability Option?</label>
                        <Dropdown v-model="confirmationResponse" :options="confirmationTypes"
                                  optionLabel="name" optionValue="code"
                                  placeholder="Select a Response"/>
                    </div>

                    <Button label="Delete" class="mr-2 mb-2" @click="deleteNonAvailability"></Button>
                    <Button label="Cancel" class="mr-2 mb-2" @click="callReset"></Button>

                </div>
            </div>
        </div>

    </div>

    <IsoEvolveFooter/>

</template>

<script>
import IsoEvolveFooter from "@/components/heading/authentication/IsoEvolveFooter.vue";
import MenuHealthCompact from "@/components/menu/health/MenuHealthCompact.vue";
import {useUserStore} from "@/stores/UserStore";
import UserService from "@/service/UserService";
import {FilterMatchMode, FilterOperator} from "primevue/api";
import CompanyService from "@/service/CompanyService";
import numeral from "numeral";
import AvailabilityService from "@/service/AvailabilityService";
import LocationService from "@/service/LocationService";
import PractitionerService from "@/service/PractitionerService";
import BillingPlanService from "@/service/BillingPlanService";
import moment from "moment/moment";
import MenuFinancialCompact from "@/components/menu/famodule/MenuFinancialCompact.vue";

export default {
    components: {
        MenuFinancialCompact,
        IsoEvolveFooter, MenuHealthCompact
    },
    setup() {
        const userStore = useUserStore()

        return {
            userStore
        }
    },
    created() {
        this.initFilters();
    },
    async mounted() {
        await this.loadCompanyContext();

    },
    computed: {
        parsedStartDate: {
            get() {
                return new Date(this.newNonAvailability.startDate);
            },
            set(value) {
                this.newNonAvailability.startDate = value instanceof Date ? value.toISOString() : '';
            }
        },
        parsedEndDate: {
            get() {
                return new Date(this.newNonAvailability.endDate);
            },
            set(value) {
                this.newNonAvailability.endDate = value instanceof Date ? value.toISOString() : '';
            }
        },
        parsedExistingStartDate: {
            get() {
                return new Date(this.existingNonAvailability.startDate);
            },
            set(value) {
                this.existingNonAvailability.startDate = value instanceof Date ? value.toISOString() : '';
            }
        },
        parsedExistingEndDate: {
            get() {
                return new Date(this.existingNonAvailability.endDate);
            },
            set(value) {
                this.existingNonAvailability.endDate = value instanceof Date ? value.toISOString() : '';
            }
        },
    },
    data() {

        return {
            profile: {},
            activeTab: 1,
            companyConfiguration: {},
            userConfiguration: {},
            filteredCodes: [],
            debounceId: null,
            abortCtrl: null,
            requestSeq: 0,
            filters: null,
            loading: true,
            submitted: false,
            filteredAvailabilityItemsCache: new Map(),
            filteredNonAvailabilityItemsCache: new Map(),
            availability: [],
            originalAvailability: [],
            nonAvailability: [],
            originalNonAvailability: [],
            newAvailability: {},
            existingAvailability: {},
            newNonAvailability: {},
            existingNonAvailability: {},
            confirmationResponse: 'N',
            selectedColumns: null,
            columnsDefault: null,
            columnsFullList: null,
            selectedStandardFilter: null,
            standardFilters: null,
            locations: [],
            practitioners: [],
            billingPlans: [],
            filteredLocations: [],
            filteredPractitioners: [],
            filteredBillingPlans: [],
            confirmationTypes: [
                {name: 'Yes', code: 'Y'},
                {name: 'No', code: 'N'}
            ],
            dayOfWeekTypes: [
                {name: 'Monday', code: 1},
                {name: 'Tuesday', code: 2},
                {name: 'Wednesday', code: 3},
                {name: 'Thursday', code: 4},
                {name: 'Friday', code: 5},
                {name: 'Saturday', code: 6},
                {name: 'Sunday', code: 7},
            ],
            statusTypes: [
                {name: 'Active', code: 'A'},
                {name: 'Inactive', code: 'I'}
            ]
        }
    },
    watch: {
        availability() {
            this.filteredAvailabilityItemsCache.clear();
        },
        nonAvailability() {
            this.filteredNonAvailabilityItemsCache.clear();
        },
        'userStore.activeCompanyId': {
            immediate: false,
            async handler(newCompanyId, oldCompanyId) {
                if (!newCompanyId || newCompanyId === oldCompanyId) {
                    return;
                }

                console.log(
                    '[Appointments][watch] Company changed:',
                    oldCompanyId,
                    '→',
                    newCompanyId
                );

                await this.loadCompanyContext();
            }
        }
    },
    methods: {
        async loadCompanyContext() {
            try {
                // Attempt to get user data
                const userData = await UserService.getUser(this.userStore.user.id);
                this.profile = userData.data;
            } catch (e) {
                console.error("[AppointmentsNew][mounted] Error fetching user data = ", e);
                this.userStore.clearUser();
                this.$router.push('login');
                return;
            }

            this.profile.companyId = this.userStore.activeCompanyId;

            try {
                const [userConfigurationData, companyConfigurationData, availabilityData] = await Promise.all([
                    UserService.getUserConfig(this.userStore.user.id),
                    CompanyService.getCompanyConfiguration(this.profile.companyId),
                    AvailabilityService.getCompanyAvailability(this.profile.companyId)
                ]);

                this.userConfiguration = userConfigurationData.data;
                this.companyConfiguration = companyConfigurationData.data;

                this.availability = availabilityData.data.filter(item => item.type === 'A')
                this.originalAvailability = [...this.availability];

                this.nonAvailability = availabilityData.data.filter(item => item.type === 'N')
                this.originalNonAvailability = [...this.nonAvailability];

                console.log("[ConfigurationAvailability][mounted] this.availability = ", this.availability);
                console.log("[ConfigurationAvailability][mounted] this.nonAvailability = ", this.nonAvailability);

                const [practitioners, locations, billingPlans] = await Promise.all([
                    PractitionerService.getCompanyPractitioners(this.profile.companyId),
                    LocationService.getCompanyLocations(this.profile.companyId),
                    BillingPlanService.getActiveBillingPlans(this.profile.companyId)
                ]);

                this.practitioners = practitioners.data;
                this.locations = locations.data;
                this.billingPlans = billingPlans.data;

                this.loading = false;

            } catch (e) {
                console.error("[ConfigurationAvailability][mounted] Error fetching additional data = ", e);
            }

            console.log("[ConfigurationAvailability][mounted] this.availability = ", this.availability);
            console.log("[ConfigurationAvailability][mounted] this.userConfiguration = ", this.userConfiguration);
            console.log("[ConfigurationAvailability][mounted] this.companyConfiguration = ", this.companyConfiguration);

            if (this.companyConfiguration.type === 'H') {
                this.columnsDefault = [
                    {field: 'locationName', header: 'Location', sortable: true},
                    {field: 'practitionerName', header: 'Practitioner', sortable: true},
                    {field: 'billingPlanName', header: 'Billing Plan', sortable: true},
                    {field: 'dayOfWeek', header: 'Day of Week', sortable: true},
                    {field: 'startHour', header: 'Start Hour', sortable: true},
                    {field: 'endHour', header: 'End Hour', sortable: true},
                    {field: 'status', header: 'Status', sortable: true}
                ];

                this.columnsFullList = [
                    {field: 'id', header: 'ID', sortable: true},
                    {field: 'locationName', header: 'Location', sortable: true},
                    {field: 'practitionerName', header: 'Practitioner', sortable: true},
                    {field: 'billingPlanName', header: 'Billing Plan', sortable: true},
                    {field: 'dayOfWeek', header: 'Day of Week', sortable: true},
                    {field: 'startHour', header: 'Start Hour', sortable: true},
                    {field: 'endHour', header: 'End Hour', sortable: true},
                    {field: 'status', header: 'Status', sortable: true}
                ];

            } else {
                this.columnsDefault = [
                    {field: 'locationName', header: 'Location', sortable: true},
                    {field: 'billingPlanName', header: 'Billing Plan', sortable: true},
                    {field: 'dayOfWeek', header: 'Day of Week', sortable: true},
                    {field: 'startHour', header: 'Start Hour', sortable: true},
                    {field: 'endHour', header: 'End Hour', sortable: true},
                    {field: 'status', header: 'Status', sortable: true}
                ];

                this.columnsFullList = [
                    {field: 'id', header: 'ID', sortable: true},
                    {field: 'locationName', header: 'Location', sortable: true},
                    {field: 'billingPlanName', header: 'Billing Plan', sortable: true},
                    {field: 'dayOfWeek', header: 'Day of Week', sortable: true},
                    {field: 'startHour', header: 'Start Hour', sortable: true},
                    {field: 'endHour', header: 'End Hour', sortable: true},
                    {field: 'status', header: 'Status', sortable: true}
                ];
            }

            this.selectedColumns = this.columnsDefault;

            this.standardFilters = [
                {field: 'active', header: 'Active'},
                {field: 'inactive', header: 'Inactive'}
            ];
        },
        formatDate(date) {
            if (!date) return ''

            const d = new Date(date)

            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')

            const hours = String(d.getHours()).padStart(2, '0')
            const minutes = String(d.getMinutes()).padStart(2, '0')

            return `${year}-${month}-${day} ${hours}:${minutes}`
        },
        formatForBackend(date) {
            if (!date) return null

            const d = new Date(date)

            const year = d.getFullYear()
            const month = String(d.getMonth() + 1).padStart(2, '0')
            const day = String(d.getDate()).padStart(2, '0')

            const hours = String(d.getHours()).padStart(2, '0')
            const minutes = String(d.getMinutes()).padStart(2, '0')

            return `${year}-${month}-${day} ${hours}:${minutes}`
        },
        initFilters() {
            this.filters = {
                'global': {value: null, matchMode: FilterMatchMode.CONTAINS},
                'name': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                },
                'description': {
                    operator: FilterOperator.AND,
                    constraints: [{value: null, matchMode: FilterMatchMode.STARTS_WITH}]
                }
            }
        },
        clearFilter() {
            this.initFilters();
        },
        restoreToOriginal() {
            window.location.reload();
        },
        callReset() {
            this.activeTab = 1;
        },
        callNewAvailability() {
            this.newAvailability = {};
            this.newAvailability.dayOfWeek = 1;
            this.newAvailability.startHour = 9;
            this.newAvailability.endHour = 12;
            this.newAvailability.status = 'A';
            this.newAvailability.type = 'A';
            this.activeTab = 4;
        },
        callNewNonAvailability() {
            this.newNonAvailability = {};
            this.newNonAvailability.status = 'A';
            this.newNonAvailability.type = 'N';
            this.newNonAvailability.startDate = moment().startOf('hour').toDate();
            this.newNonAvailability.endDate = moment().startOf('hour').toDate();
            this.activeTab = 5;
        },
        callUpdateAvailability(data) {
            this.activeTab = 2;
            this.existingAvailability = data;
        },
        callDeleteAvailability(data) {
            this.activeTab = 3;
            this.confirmationResponse = 'N';
            this.existingAvailability = data;
        },
        callUpdateNonAvailability(data) {
            this.activeTab = 6;
            this.existingNonAvailability = data;
        },
        callDeleteNonAvailability(data) {
            this.activeTab = 7;
            this.confirmationResponse = 'N';
            this.existingNonAvailability = data;
        },
        async callViewAvailability() {
            this.activeTab = 1;
        },
        onToggleColumns(value) {
            this.selectedColumns = this.columnsFullList.filter(col => value.includes(col));
        },
        onToggleStandardFilters(value) {

            if (value.length > 1) {
                // Keep the last selected option
                this.selectedStandardFilter = [value[value.length - 1]];
            } else {
                this.selectedStandardFilter = value;
            }

            const isActive = value.some(item => item.field === "active");
            const isInactive = value.some(item => item.field === "inactive");

            if (this.availability !== null) {

                if (isActive) {
                    this.availability = this.originalAvailability.filter(availability => availability.status === 'A');
                } else if (isInactive) {
                    this.availability = this.originalAvailability.filter(availability => availability.status === 'I');
                } else {
                    this.availability = this.originalAvailability;
                }

            } else {
                // Handle the case when auditTrails is null
                this.availability = null;
            }
        },
        getFilteredAvailabilityItems(data) {
            if (this.filteredAvailabilityItemsCache.has(data.id)) {
                return this.filteredAvailabilityItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateAvailability(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteAvailability(data);
                }
            });

            this.filteredAvailabilityItemsCache.set(data.id, items);

            return items;
        },
        getFilteredNonAvailabilityItems(data) {
            if (this.filteredNonAvailabilityItemsCache.has(data.id)) {
                return this.filteredNonAvailabilityItemsCache.get(data.id);
            }

            const items = [];

            items.push({
                label: 'Update',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callUpdateNonAvailability(data);
                }
            });

            items.push({
                label: 'Delete',
                class: 'mb-4 mr-4 ml-4 mt-4',
                command: () => {
                    this.callDeleteNonAvailability(data);
                }
            });

            this.filteredNonAvailabilityItemsCache.set(data.id, items);

            return items;
        },
        formatAmount(value) {
            if (value) {
                return numeral(String(value)).format('0,0.00');
            }
        },
        searchLocations(event) {
            console.log("[Appointments][searchLocations] event.query = ", event.query);

            const q = (event?.query ?? '').trim().toLowerCase();

            if (!q) {
                this.filteredLocations = (this.locations ?? []).slice(0, 50); // cap if large
                return;
            }

            LocationService.searchCompanyLocations(this.profile.companyId, event.query.toLowerCase())
                .then(data => {
                    console.log("[ConfigurationAvailability][searchLocations] Getting location data for company = ", this.profile.companyId);
                    this.filteredLocations = data.data;
                    console.log("[ConfigurationAvailability][searchLocations] Got location data = ", data.data);
                })
                .catch(e => {
                    this.filteredLocations = [];

                    console.log("[ConfigurationAvailability][searchLocations] Could not get location data for user = ", this.profile.companyId);
                    console.log("[ConfigurationAvailability][searchLocations] Error = ", e);
                });

        },
        searchPractitioners(event) {
            console.log("[ConfigurationAvailability][searchPractitioners] event.query = ", event.query);

            const q = (event?.query ?? '').trim().toLowerCase();

            if (!q) {
                this.filteredPractitioners = (this.practitioners ?? []).slice(0, 50); // cap if large
                return;
            }

            PractitionerService.searchCompanyPractitioners(this.profile.companyId, q)
                .then(data => {
                    console.log("[ConfigurationAvailability][searchPractitioners] Getting practitioner data for company = ", this.profile.companyId);
                    this.filteredPractitioners = data.data;
                    console.log("[ConfigurationAvailability][searchPractitioners] Got practitioner data = ", data.data);
                })
                .catch(e => {
                    console.log("[ConfigurationAvailability][searchPractitioners] Could not get practitioner data for company = ", this.profile.companyId);
                    console.log("[ConfigurationAvailability][searchPractitioners] Error = ", e);
                    this.filteredPractitioners = [];
                });

        },
        searchBillingPlans(event) {
            console.log("[ConfigurationAvailability][searchBillingPlans] event.query = ", event.query);

            const q = (event?.query ?? '').trim().toLowerCase();

            if (!q) {
                this.filteredBillingPlans = (this.billingPlans ?? []).slice(0, 50); // cap if large
                return;
            }

            BillingPlanService.getActiveBillingPlans(this.profile.companyId)
                .then(response => {
                    const plans = response.data || [];

                    this.filteredBillingPlans = plans.filter(plan =>
                        plan.name?.toLowerCase().includes(q)
                    );

                    console.log("[ConfigurationAvailability][searchBillingPlan] Got billing plan data = ", this.filteredBillingPlans);
                })
                .catch(e => {
                    this.filteredBillingPlans = [];

                    console.log("[ConfigurationAvailability][searchBillingPlan] Could not get billing plan data for user = ", this.profile.companyId);
                    console.log("[ConfigurationAvailability][searchBillingPlan] Error = ", e);
                });

        },
        async createAvailability() {
            this.submitted = true;

            if (this.newAvailability.dayOfWeek) {

                try {
                    var availabilityDto = {
                        locationId: this.newAvailability.location.id,
                        practitionerId: this.newAvailability.practitioner.id,
                        billingPlanId: this.newAvailability.billingPlan.id,
                        dayOfWeek: this.newAvailability.dayOfWeek,
                        startHour: this.newAvailability.startHour,
                        endHour: this.newAvailability.endHour,
                        type: 'A',
                        status: 'A'
                    };

                    console.log('[ConfigurationAvailability][createAvailability] availabilityDto = ', availabilityDto);

                    const response = await AvailabilityService.createAvailability(
                        this.profile.companyId, availabilityDto);
                    console.log('[ConfigurationAvailability][createAvailability] Response = ', response);

                    const [availabilityData] = await Promise.all([
                        AvailabilityService.getCompanyAvailability(this.profile.companyId)
                    ]);

                    this.availability = availabilityData.data.filter(item => item.type === 'A')
                    this.originalAvailability = [...this.availability];

                    this.nonAvailability = availabilityData.data.filter(item => item.type === 'N')
                    this.originalNonAvailability = [...this.nonAvailability];

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created availability slot',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ConfigurationAvailability][createAvailability] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create availability slot. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async createNonAvailability() {
            this.submitted = true;

            if (this.newNonAvailability.startDate) {

                var formattedStartDate = moment(this.newNonAvailability.startDate).format('YYYY-MM-DD HH:mm');
                var formattedEndDate = moment(this.newNonAvailability.endDate).format('YYYY-MM-DD HH:mm');

                try {
                    var availabilityDto = {
                        locationId: this.newNonAvailability.location.id,
                        practitionerId: this.newNonAvailability.practitioner.id,
                        billingPlanId: this.newNonAvailability.billingPlan.id,
                        startDate: formattedStartDate,
                        endDate: formattedEndDate,
                        dayOfWeek: 1,
                        startHour: 0,
                        endHour: 0,
                        type: 'N',
                        status: 'A'
                    };

                    console.log('[ConfigurationAvailability][createNonAvailability] availabilityDto = ', availabilityDto);

                    const response = await AvailabilityService.createAvailability(
                        this.profile.companyId, availabilityDto);
                    console.log('[ConfigurationAvailability][createNonAvailability] Response = ', response);

                    const [availabilityData] = await Promise.all([
                        AvailabilityService.getCompanyAvailability(this.profile.companyId)
                    ]);

                    this.availability = availabilityData.data.filter(item => item.type === 'A')
                    this.originalAvailability = [...this.availability];

                    this.nonAvailability = availabilityData.data.filter(item => item.type === 'N')
                    this.originalNonAvailability = [...this.nonAvailability];

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Created non-availability slot',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ConfigurationAvailability][createNonAvailability] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to create non-availability slot. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async updateAvailability() {
            this.submitted = true;

            if (this.existingAvailability.id) {

                try {

                    var tempLocation = this.existingAvailability.locationName;
                    var tempPractitioner = this.existingAvailability.practitionerName;
                    var tempBillingPlan = this.existingAvailability.billingPlanName;

                    try {
                        if (this.existingAvailability.locationName.id == null) {
                            tempLocation = this.existingAvailability.locationId;
                        } else {
                            tempLocation = this.existingAvailability.locationName.id;
                        }
                    } catch (error) {
                        tempLocation = this.existingAvailability.locationId;
                    }

                    try {
                        if (this.existingAvailability.practitionerName.id == null) {
                            tempPractitioner = this.existingAvailability.practitionerId;
                        } else {
                            tempPractitioner = this.existingAvailability.practitionerName.id;
                        }
                    } catch (error) {
                        tempPractitioner = this.existingAvailability.practitionerId;
                    }

                    try {
                        if (this.existingAvailability.billingPlanName.id == null) {
                            tempBillingPlan = this.existingAvailability.billingPlanId;
                        } else {
                            tempBillingPlan = this.existingAvailability.billingPlanName.id;
                        }
                    } catch (error) {
                        tempBillingPlan = this.existingAvailability.billingPlanId;
                    }

                    var availabilityDto = {
                        locationId: tempLocation,
                        practitionerId: tempPractitioner,
                        billingPlanId: tempBillingPlan,
                        dayOfWeek: this.existingAvailability.dayOfWeek,
                        startHour: this.existingAvailability.startHour,
                        endHour: this.existingAvailability.endHour,
                        status: this.existingAvailability.status
                    };

                    console.log('[ConfigurationAvailability][updateAvailability] availabilityDto = ', availabilityDto);

                    const response = await AvailabilityService.updateAvailability(
                        this.existingAvailability.id, availabilityDto);
                    console.log('[ConfigurationAvailability][updateAvailability] Response = ', response);

                    const [availabilityData] = await Promise.all([
                        AvailabilityService.getCompanyAvailability(this.profile.companyId)
                    ]);

                    this.availability = availabilityData.data.filter(item => item.type === 'A')
                    this.originalAvailability = [...this.availability];

                    this.nonAvailability = availabilityData.data.filter(item => item.type === 'N')
                    this.originalNonAvailability = [...this.nonAvailability];

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated availability slot',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ConfigurationAvailability][updateAvailability] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update availability slot. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteAvailability() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.existingAvailability.id) {

                    try {
                        const response = await AvailabilityService.deleteAvailability(this.existingAvailability.id);
                        console.log('[ConfigurationAvailability][deleteAvailability] Response = ', response);

                        const [availabilityData] = await Promise.all([
                            AvailabilityService.getCompanyAvailability(this.profile.companyId)
                        ]);

                        this.availability = availabilityData.data.filter(item => item.type === 'A')
                        this.originalAvailability = [...this.availability];

                        this.nonAvailability = availabilityData.data.filter(item => item.type === 'N')
                        this.originalNonAvailability = [...this.nonAvailability];

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted availability slot',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[ConfigurationAvailability][deleteAvailability] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete availability slot. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async updateNonAvailability() {
            this.submitted = true;

            if (this.existingNonAvailability.id) {

                try {

                    var tempLocation = this.existingNonAvailability.locationName;
                    var tempPractitioner = this.existingNonAvailability.practitionerName;
                    var tempBillingPlan = this.existingNonAvailability.billingPlanName;

                    try {
                        if (this.existingNonAvailability.locationName.id == null) {
                            tempLocation = this.existingNonAvailability.locationId;
                        } else {
                            tempLocation = this.existingNonAvailability.locationName.id;
                        }
                    } catch (error) {
                        tempLocation = this.existingNonAvailability.locationId;
                    }

                    try {
                        if (this.existingNonAvailability.practitionerName.id == null) {
                            tempPractitioner = this.existingNonAvailability.practitionerId;
                        } else {
                            tempPractitioner = this.existingNonAvailability.practitionerName.id;
                        }
                    } catch (error) {
                        tempPractitioner = this.existingNonAvailability.practitionerId;
                    }

                    try {
                        if (this.existingNonAvailability.billingPlanName.id == null) {
                            tempBillingPlan = this.existingNonAvailability.billingPlanId;
                        } else {
                            tempBillingPlan = this.existingNonAvailability.billingPlanName.id;
                        }
                    } catch (error) {
                        tempBillingPlan = this.existingNonAvailability.billingPlanId;
                    }

                    var availabilityDto = {
                        locationId: tempLocation,
                        practitionerId: tempPractitioner,
                        billingPlanId: tempBillingPlan,
                        dayOfWeek: this.existingNonAvailability.dayOfWeek,
                        startHour: this.existingNonAvailability.startHour,
                        endHour: this.existingNonAvailability.endHour,
                        startDate: this.formatForBackend(this.existingNonAvailability.startDate),
                        endDate: this.formatForBackend(this.existingNonAvailability.endDate),
                        status: this.existingNonAvailability.status
                    };

                    console.log('[ConfigurationAvailability][updateNonAvailability] availabilityDto = ', availabilityDto);

                    const response = await AvailabilityService.updateAvailability(
                        this.existingNonAvailability.id, availabilityDto);
                    console.log('[ConfigurationAvailability][updateNonAvailability] Response = ', response);

                    const [availabilityData] = await Promise.all([
                        AvailabilityService.getCompanyAvailability(this.profile.companyId)
                    ]);

                    this.availability = availabilityData.data.filter(item => item.type === 'A')
                    this.originalAvailability = [...this.availability];

                    this.nonAvailability = availabilityData.data.filter(item => item.type === 'N')
                    this.originalNonAvailability = [...this.nonAvailability];

                    this.$toast.add({
                        severity: 'success',
                        summary: 'Successful',
                        detail: 'Updated non-availability slot',
                        life: 3000
                    });

                } catch (error) {
                    console.error('[ConfigurationAvailability][updateNonAvailability] Error:', error);

                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to update non-availability slot. Please try again.',
                        life: 3000
                    });
                }
            }

            this.activeTab = 1;
            this.submitted = false;
        },
        async deleteNonAvailability() {
            this.submitted = true;

            if (this.confirmationResponse.trim() === "Y") {

                if (this.existingNonAvailability.id) {

                    try {
                        const response = await AvailabilityService.deleteAvailability(this.existingNonAvailability.id);
                        console.log('[ConfigurationAvailability][deleteNonAvailability] Response = ', response);

                        const [availabilityData] = await Promise.all([
                            AvailabilityService.getCompanyAvailability(this.profile.companyId)
                        ]);

                        this.availability = availabilityData.data.filter(item => item.type === 'A')
                        this.originalAvailability = [...this.availability];

                        this.nonAvailability = availabilityData.data.filter(item => item.type === 'N')
                        this.originalNonAvailability = [...this.nonAvailability];

                        this.$toast.add({
                            severity: 'success',
                            summary: 'Successful',
                            detail: 'Deleted non-availability slot',
                            life: 3000
                        });

                    } catch (error) {
                        console.error('[ConfigurationAvailability][deleteNonAvailability] Error:', error);

                        this.$toast.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Failed to delete non-availability slot. Please try again.',
                            life: 3000
                        });
                    }
                }

            }

            this.activeTab = 1;
            this.submitted = false;
        }
    }
};
</script>

<style scoped lang="scss">
a {
    text-decoration: none;
}

/* Center header text + sort icons */
::v-deep(.p-datatable-frozen-tbody) {
    font-weight: bold;
}

::v-deep(.p-datatable-scrollable .p-frozen-column) {
    font-weight: bold;
}

::v-deep(.p-progressbar) {
    height: .5rem;
    background-color: #D8DADC;

    .p-progressbar-value {
        background-color: #607D8B;
    }
}

.status-badge, .confirmation-badge, .submission-badge {
    border-radius: var(--border-radius);
    padding: .25em .5rem;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 12px;
    letter-spacing: .3px;
}

.status-badge {
    &.status-A {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-I {
        background: #FFCDD2;
        color: #C63737;
    }

}

.confirmation-badge {
    &.status-N {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-Y {
        background: #C8E6C9;
        color: #256029;
    }
}

.submission-badge {
    &.status-1 {
        background: #C8E6C9;
        color: #256029;
    }

    &.status-2 {
        background: #FFCDD2;
        color: #C63737;
    }

    &.status-3 {
        background: #FEEDAF;
        color: #8A5340;
    }

    &.status-4 {
        background: #B3E5FC;
        color: #23547B;
    }

    &.status-5 {
        background: #ECCFFF;
        color: #694382;
    }

    &.status-6 {
        background: #d9b78a;
        color: #b94f03;
    }

    &.status-7 {
        background: #b9a1de;
        color: #830293;
    }
}


.tabview-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-tabview p {
    line-height: 1.5;
    margin: 0;
}

.accordion-custom {
    i, span {
        vertical-align: middle;
    }

    span {
        margin: 0 .5rem;
    }
}

.p-accordion p {
    line-height: 1.5;
    margin: 0;
}

.mb-2 {
    margin-bottom: 1rem;
}

.mr-2 {
    margin-right: 1rem;
}

</style>
